import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Button, Chip, Divider, Group, Modal, SimpleGrid, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import {
  RESPONSIBILITY_LABELS,
  RESPONSIBILITY_ORDER,
  type CareContact,
  type ContactResponsibility,
} from '../../lib/api/domains/customerCare';

const EMPTY_FORM = { name: '', jobRole: '', email: '', birthday: '', notes: '' };
const EMPTY_PHONE = { label: '', number: '' };

/**
 * Ansprechpartner anlegen oder bearbeiten: Stammdaten, Rufnummern, Zuständigkeiten.
 *
 * Eine Stelle für Kundeninfos und Portal-Sicht — zwei Formulare für denselben Datensatz
 * liefen früher oder später auseinander. Portal-Zugang und -Rechte gehören bewusst NICHT
 * hierher (Freischalten bzw. „Rolle & Pakete").
 */
export function CareContactFormModal({
  opened,
  customerId,
  contact,
  onClose,
  onSaved,
  zIndex = 800,
}: {
  opened: boolean;
  /** Lokale Kunden-UUID — gebraucht beim Anlegen. */
  customerId: string;
  /** null = neuen Ansprechpartner anlegen */
  contact: CareContact | null;
  onClose: () => void;
  /** Nach erfolgreichem Speichern, z. B. um eine andere Ansicht neu zu laden. */
  onSaved?: () => void;
  zIndex?: number;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  /** Portal-interne Zuständigkeiten (Haupt-AP + Themen). */
  const [responsibilities, setResponsibilities] = useState<ContactResponsibility[]>([]);
  /** Rufnummern-Zeilen — die erste Nummer wird serverseitig zur Hauptnummer (Anrufer-Matching). */
  const [phoneRows, setPhoneRows] = useState([EMPTY_PHONE]);

  // Befüllt wird beim Öffnen bzw. beim Wechsel auf einen anderen Kontakt — NICHT bei jedem
  // Refetch: sonst überschriebe ein Hintergrund-Update die gerade getippten Werte.
  const contactRef = useRef(contact);
  contactRef.current = contact;
  const contactId = contact?.id ?? null;
  useEffect(() => {
    if (!opened) return;
    const c = contactRef.current;
    setForm(
      c
        ? { name: c.name, jobRole: c.jobRole ?? '', email: c.email ?? '', birthday: c.birthday ?? '', notes: c.notes ?? '' }
        : EMPTY_FORM,
    );
    setPhoneRows(
      c && c.phones.length > 0 ? c.phones.map((p) => ({ label: p.label ?? '', number: p.number })) : [EMPTY_PHONE],
    );
    setResponsibilities(c?.responsibilities ?? []);
  }, [opened, contactId]);

  const save = useMutation({
    mutationFn: () => {
      // Leere Felder werden bewusst als '' mitgeschickt — der Server löscht sie dann
      // (PATCH-Semantik: weglassen = unverändert, '' = entfernen).
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        jobRole: form.jobRole.trim(),
        birthday: form.birthday,
        notes: form.notes,
        phones: phoneRows
          .map((r) => ({ label: r.label.trim() || undefined, number: r.number.trim() }))
          .filter((r) => r.number.length > 0),
        responsibilities,
      };
      return contact ? api.updateCareContact(contact.id, payload) : api.createCareContact(customerId, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer-care'] });
      void queryClient.invalidateQueries({ queryKey: ['hub-customer-context'] });
      notifications.show({ title: 'Ansprechpartner gespeichert', message: '', color: 'green' });
      onSaved?.();
      onClose();
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={contact ? `${contact.name} bearbeiten` : 'Ansprechpartner hinzufügen'}
      zIndex={zIndex}
      size="lg"
      centered
    >
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Name"
            required
            data-autofocus
            value={form.name}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, name: value }));
            }}
          />
          <TextInput
            label="Position"
            placeholder="z. B. IT-Verantwortliche, Geschäftsführung"
            value={form.jobRole}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, jobRole: value }));
            }}
          />
        </SimpleGrid>

        <Divider label="Erreichbarkeit" labelPosition="left" />
        <TextInput
          label="E-Mail"
          type="email"
          value={form.email}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setForm((f) => ({ ...f, email: value }));
          }}
          description="Für die Absender-Zuordnung, das Kontaktverzeichnis und die Anmeldung im Kundenportal."
        />
        <div>
          <Text size="sm" fw={500} mb={4}>
            Rufnummern
          </Text>
          <Stack gap={6}>
            {phoneRows.map((row, i) => (
              <Group key={i} gap="xs" wrap="nowrap">
                <TextInput
                  placeholder="Nummer"
                  aria-label={`Rufnummer ${i + 1}`}
                  value={row.number}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setPhoneRows((rows) => rows.map((r, j) => (j === i ? { ...r, number: v } : r)));
                  }}
                  style={{ flex: 1.2 }}
                  size="sm"
                />
                <TextInput
                  placeholder="Bezeichnung (Mobil, Zentrale, …)"
                  aria-label={`Bezeichnung für Rufnummer ${i + 1}`}
                  value={row.label}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setPhoneRows((rows) => rows.map((r, j) => (j === i ? { ...r, label: v } : r)));
                  }}
                  style={{ flex: 1 }}
                  size="sm"
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    setPhoneRows((rows) => {
                      const next = rows.filter((_, j) => j !== i);
                      return next.length > 0 ? next : [EMPTY_PHONE];
                    })
                  }
                  aria-label="Rufnummer entfernen"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            ))}
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconPlus size={12} />}
              onClick={() => setPhoneRows((rows) => [...rows, EMPTY_PHONE])}
              style={{ alignSelf: 'flex-start' }}
            >
              Weitere Rufnummer
            </Button>
          </Stack>
          <Text size="xs" c="dimmed" mt={4}>
            Die erste Nummer ist die Hauptnummer — sie wird für die Anrufer-Erkennung genutzt.
          </Text>
        </div>

        <Divider label="Zuständigkeiten" labelPosition="left" />
        <div>
          <Chip.Group multiple value={responsibilities} onChange={(value) => setResponsibilities(value as ContactResponsibility[])}>
            <Group gap={6}>
              {RESPONSIBILITY_ORDER.map((r) => (
                <Chip key={r} value={r} size="xs" variant="light" color={r === 'PRIMARY' ? 'cyberOrange' : 'grape'}>
                  {RESPONSIBILITY_LABELS[r]}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          <Text size="xs" c="dimmed" mt={6}>
            Mehrfachauswahl. „Haupt-AP" dürfen mehrere Personen sein — etwa je Standort oder
            Gesellschaft.
          </Text>
        </div>

        <Divider label="Weiteres" labelPosition="left" />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Geburtstag"
            type="date"
            value={form.birthday}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, birthday: value }));
            }}
          />
        </SimpleGrid>
        <Textarea
          label="Hinweise"
          placeholder="Erreichbarkeit, Zuständigkeiten, Besonderheiten …"
          value={form.notes}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setForm((f) => ({ ...f, notes: value }));
          }}
          autosize
          minRows={2}
          maxRows={6}
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={form.name.trim().length < 2} loading={save.isPending} onClick={() => save.mutate()}>
            Speichern
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
